import { create } from 'zustand';
import type { Place, BBox } from '@wingman/shared';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '@wingman/shared';

export type MapTheme = 'cyberpunk' | 'conventional';

export interface MapFilters {
  openNow: boolean;
  minRating: number;
  sortBy: 'distance' | 'rating' | 'name';
}

interface MapState {
  center: { lat: number; lng: number };
  zoom: number;
  bbox: BBox | null;
  places: Place[];
  selectedPlace: Place | null;
  loadingPlaces: boolean;
  /** Set when a bbox fetch fails. Cleared on next successful fetch. */
  fetchError: string | null;
  activeCategory: string | null;
  filters: MapFilters;
  showFilters: boolean;
  mapTheme: MapTheme;
  flyToTarget: { lat: number; lng: number; zoom?: number } | null;
  setCenter: (lat: number, lng: number) => void;
  setZoom: (zoom: number) => void;
  /** Updates bbox and derives center from it so both stay in sync */
  setBBox: (bbox: BBox, zoom?: number) => void;
  setPlaces: (places: Place[]) => void;
  setSelectedPlace: (place: Place | null) => void;
  setLoadingPlaces: (loading: boolean) => void;
  setFetchError: (error: string | null) => void;
  setActiveCategory: (category: string | null) => void;
  setFilters: (filters: Partial<MapFilters>) => void;
  toggleFilters: () => void;
  setMapTheme: (theme: MapTheme) => void;
  setFlyToTarget: (target: { lat: number; lng: number; zoom?: number } | null) => void;
}

export const useMapStore = create<MapState>((set) => ({
  center: DEFAULT_MAP_CENTER,
  zoom: DEFAULT_MAP_ZOOM,
  bbox: null,
  places: [],
  selectedPlace: null,
  loadingPlaces: false,
  fetchError: null,
  activeCategory: null,
  filters: { openNow: false, minRating: 0, sortBy: 'distance' },
  showFilters: false,
  mapTheme: 'cyberpunk',
  flyToTarget: null,

  setCenter: (lat, lng) => set({ center: { lat, lng } }),
  setZoom: (zoom) => set({ zoom }),

  // Derives center automatically so MapStore.center always reflects reality
  setBBox: (bbox, zoom) =>
    set((state) => ({
      bbox,
      zoom: zoom ?? state.zoom,
      center: {
        lat: (bbox.minLat + bbox.maxLat) / 2,
        lng: (bbox.minLng + bbox.maxLng) / 2,
      },
    })),

  setPlaces: (places) => set({ places }),
  setSelectedPlace: (place) => set({ selectedPlace: place }),
  setLoadingPlaces: (loading) => set({ loadingPlaces: loading }),
  setFetchError: (error) => set({ fetchError: error }),
  setActiveCategory: (category) => set({ activeCategory: category }),
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
  toggleFilters: () => set((state) => ({ showFilters: !state.showFilters })),
  setMapTheme: (theme) => set({ mapTheme: theme }),
  setFlyToTarget: (target) => set({ flyToTarget: target }),
}));
